import { useEffect, useState, useCallback } from "react";
import { Plus, Building2, Briefcase, Edit2, Trash2, Check, X, ArrowRightLeft, BarChart3, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import WorkspaceTearSheetDialog from "../components/WorkspaceTearSheetDialog";

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function PlanGate({ plan }) {
  return (
    <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-10 text-center" data-testid="workspaces-plan-gate">
      <div className="w-14 h-14 mx-auto rounded-full bg-amber-500 text-white flex items-center justify-center mb-4">
        <Lock className="w-6 h-6" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
        Multi-account workspaces
      </h2>
      <p className="text-slate-700 max-w-md mx-auto">
        Manage multiple LLCs, syndications, or client portfolios from a single login.
        Your <span className="font-semibold capitalize">{plan || "current"}</span> plan doesn't include
        workspaces — upgrade to <span className="font-semibold">Fund Manager</span> ($299/mo, 5 workspaces)
        or <span className="font-semibold">Enterprise Multi</span> ($499/mo, unlimited).
      </p>
      <div className="mt-6 flex items-center justify-center gap-2">
        <a
          href="/dashboard/billing"
          data-testid="workspaces-upgrade-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
        >
          Upgrade plan
        </a>
      </div>
    </div>
  );
}

export default function Workspaces() {
  const [meta, setMeta] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ label: "", company: "" });
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(null); // {id, label}
  const [tearSheetFor, setTearSheetFor] = useState(null); // workspace row or null
  const [tab, setTab] = useState("workspaces"); // workspaces | rollup

  const load = useCallback(async () => {
    try {
      const [w, r] = await Promise.all([
        api.get("/workspaces"),
        api.get("/workspaces/rollup").catch(() => ({ data: null })),
      ]);
      setMeta(w.data);
      setRollup(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!createForm.label.trim()) {
      toast.error("Workspace name is required.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/workspaces", { label: createForm.label.trim(), company: createForm.company.trim() || null });
      toast.success("Workspace created");
      setShowCreate(false);
      setCreateForm({ label: "", company: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const rename = async (id, label) => {
    try {
      await api.put(`/workspaces/${id}`, { label });
      toast.success("Renamed");
      setRenaming(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete "${label}"? Properties and data inside this workspace will become inaccessible. This cannot be undone from the UI.`)) return;
    try {
      await api.delete(`/workspaces/${id}`);
      toast.success("Workspace deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const switchTo = async (id) => {
    try {
      const res = await api.post(`/workspaces/${id}/switch`);
      localStorage.setItem("rv_token", res.data.token);
      toast.success(`Switched to ${res.data.workspace.label}`);
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (!meta) {
    return (
      <DashboardShell>
        <div className="px-8 py-8 text-slate-500">Loading…</div>
      </DashboardShell>
    );
  }

  // Plan gate: only show for plans that have *no* workspaces (excluding the master row).
  const isAdmin = !!meta.is_workspace_session === false && (meta.max_workspaces === 0) && (meta.items.length <= 1);
  if (isAdmin) {
    return (
      <DashboardShell>
        <div className="px-8 py-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "Outfit" }} data-testid="workspaces-page-title">
            Workspaces
          </h1>
          <PlanGate plan={meta.plan} />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="px-8 py-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="workspaces-page-title">
              Workspaces
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Manage multiple isolated portfolios (LLCs, syndications, client books) from a single login.
              <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-slate-100 rounded text-xs font-semibold text-slate-700 capitalize">
                {meta.plan} plan · {meta.workspaces_used}/{meta.max_workspaces} used
              </span>
            </p>
          </div>
          {meta.can_create && !meta.is_workspace_session && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="workspaces-new-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              <Plus className="w-4 h-4" /> New workspace
            </button>
          )}
        </div>

        <div className="inline-flex bg-slate-100 rounded-md p-0.5 mb-5" data-testid="workspaces-tabs">
          {[
            { id: "workspaces", label: "Workspaces", icon: Building2 },
            { id: "rollup", label: "Portfolio rollup", icon: BarChart3 },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`workspaces-tab-${t.id}`}
              className={`px-3 py-1.5 text-sm rounded ${tab === t.id ? "bg-white text-slate-900 shadow-sm font-semibold" : "text-slate-600 hover:text-slate-900"}`}
            >
              <t.icon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "workspaces" && (
          <div className="bg-white border border-slate-200 rounded-lg" data-testid="workspaces-list">
            {meta.items.map((it) => {
              const isActive = it.id === meta.active_workspace_id;
              const isRenaming = renaming?.id === it.id;
              return (
                <div
                  key={it.id}
                  className={`px-5 py-4 flex items-center gap-4 border-b border-slate-100 last:border-b-0 ${isActive ? "bg-emerald-50/40" : ""}`}
                  data-testid={`workspaces-row-${it.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    {it.is_master ? <Building2 className="w-5 h-5 text-slate-700" /> : <Briefcase className="w-5 h-5 text-amber-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {isRenaming ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={renaming.label}
                          onChange={(e) => setRenaming({ ...renaming, label: e.target.value })}
                          autoFocus
                          data-testid={`workspaces-rename-input-${it.id}`}
                          className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                        />
                        <button
                          onClick={() => rename(it.id, renaming.label.trim())}
                          data-testid={`workspaces-rename-save-${it.id}`}
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setRenaming(null)}
                          className="p-1 rounded hover:bg-slate-100 text-slate-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          {it.label}
                          {isActive && (
                            <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-700 text-white px-1.5 py-0.5 rounded" data-testid={`workspaces-active-badge-${it.id}`}>
                              Active
                            </span>
                          )}
                          {it.is_master && (
                            <span className="text-[10px] uppercase tracking-widest font-bold bg-slate-700 text-white px-1.5 py-0.5 rounded">
                              Master
                            </span>
                          )}
                        </div>
                        {it.company && <div className="text-xs text-slate-500 mt-0.5">{it.company}</div>}
                      </>
                    )}
                  </div>
                  {!isRenaming && (
                    <div className="flex items-center gap-1">
                      {!isActive && (
                        <button
                          onClick={() => switchTo(it.id)}
                          data-testid={`workspaces-switch-${it.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800"
                        >
                          <ArrowRightLeft className="w-3 h-3" /> Switch
                        </button>
                      )}
                      <button
                        onClick={() => setTearSheetFor(it)}
                        data-testid={`workspaces-tearsheet-${it.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                        title="Generate a tear sheet or share link for this workspace"
                      >
                        <FileText className="w-3 h-3" /> Tear Sheet
                      </button>
                      {!it.is_master && !meta.is_workspace_session && (
                        <>
                          <button
                            onClick={() => setRenaming({ id: it.id, label: it.label })}
                            data-testid={`workspaces-rename-${it.id}`}
                            className="p-2 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                            title="Rename"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => remove(it.id, it.label)}
                            data-testid={`workspaces-delete-${it.id}`}
                            className="p-2 rounded text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "rollup" && rollup && (
          <div className="space-y-4" data-testid="workspaces-rollup">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: "Properties", value: rollup.totals.properties, color: "slate" },
                { label: "Active leases", value: rollup.totals.active_leases, color: "emerald" },
                { label: "Open work orders", value: rollup.totals.open_workorders, color: "rose" },
                { label: "Monthly rent", value: fmtMoney(rollup.totals.monthly_rent), color: "indigo" },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`workspaces-rollup-kpi-${kpi.label.toLowerCase().replace(/ /g,"-")}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{kpi.label}</div>
                  <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>{kpi.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 text-xs uppercase tracking-widest font-bold text-slate-600 border-b border-slate-100">Per-workspace breakdown</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Workspace</th>
                    <th className="px-4 py-2 text-right">Props</th>
                    <th className="px-4 py-2 text-right">Active leases</th>
                    <th className="px-4 py-2 text-right">Open WOs</th>
                    <th className="px-4 py-2 text-right">Rent / mo</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.workspaces.map((w) => (
                    <tr key={w.id} className="border-t border-slate-100" data-testid={`workspaces-rollup-row-${w.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          {w.is_master ? <Building2 className="w-3.5 h-3.5 text-slate-500" /> : <Briefcase className="w-3.5 h-3.5 text-amber-700" />}
                          {w.label}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">{w.properties}</td>
                      <td className="px-4 py-2.5 text-right">{w.active_leases}</td>
                      <td className="px-4 py-2.5 text-right">{w.open_workorders}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{fmtMoney(w.monthly_rent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" data-testid="workspaces-create-dialog">
          <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-bold text-slate-900">New workspace</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Workspace name</span>
                <input
                  type="text"
                  value={createForm.label}
                  onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                  placeholder="Sunrise Fund LLC"
                  autoFocus
                  data-testid="workspaces-create-label"
                  className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Company / entity (optional)</span>
                <input
                  type="text"
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  placeholder="Sunrise Fund LLC"
                  className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </label>
              <div className="text-xs text-slate-500">
                A blank, fully isolated workspace is created. You can switch into it from the sidebar
                and start adding properties, tenants, and leases.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
              <button onClick={() => setShowCreate(false)} className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded">
                Cancel
              </button>
              <button
                onClick={create}
                disabled={creating}
                data-testid="workspaces-create-save"
                className="px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded"
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      <WorkspaceTearSheetDialog
        workspace={tearSheetFor}
        onOpenChange={(v) => !v && setTearSheetFor(null)}
      />
    </DashboardShell>
  );
}
